"use client";

import { m } from "framer-motion";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

const STEPS = [
  {
    number: "01",
    title: "We talk for 30 minutes",
    description:
      "You tell me what you're trying to do. I ask questions until I understand the real problem — not just the feature list. No contracts signed yet. Just a conversation to see if we're a fit.",
  },
  {
    number: "02",
    title: "I send a one-page scope",
    description:
      "Plain English. What I'm building, what it costs, how long it takes, and what I need from you. No 20-page proposals. If you want changes, we talk and I update it. One round of revisions, then we move.",
  },
  {
    number: "03",
    title: "Half up front, half on launch",
    description:
      "50% to start, 50% when it's live. I don't do milestone invoicing — too much paperwork, not enough building. You get weekly updates via Loom video or Slack. No mystery, no 'check in next month.'",
  },
  {
    number: "04",
    title: "Build, review, ship",
    description:
      "I work solo. No handoffs, no 'let me check with the team.' You see progress on a staging URL within the first week. We do one review cycle, I fix what needs fixing, then we push to production.",
  },
  {
    number: "05",
    title: "30 days of fixes included",
    description:
      "After launch, anything that breaks because of how I built it — I fix it free. For 30 days. No support tickets, no tiers. Just text me and I handle it.",
  },
];

const HowIWork = () => {
  return (
    <section id="process" className="relative px-4 py-24">
      <div className="section-divider mx-auto mb-20 max-w-xl" />

      <div className="mx-auto max-w-4xl">
        <SectionHeader
          index="04"
          code="process"
          align="right"
          title={<>My process. <span className="gradient-text">No surprises.</span></>}
          blurb="I’ve been burned by bad contracts. Here’s what I do instead."
        />

        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[19px] top-8 bottom-8 hidden w-px bg-white/[0.06] md:block" />

          <div className="space-y-8">
            {STEPS.map((step, i) => (
              <AnimatedSection key={step.number} delay={i * 0.08}>
                <m.div
                  whileHover={{ x: 4 }}
                  className="group relative flex gap-5 md:gap-6"
                >
                  {/* Number bubble */}
                  <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-xs font-bold text-emerald-400 ring-1 ring-emerald-500/20 font-[family-name:var(--font-jetbrains-mono)]">
                    {step.number}
                  </div>

                  {/* Content */}
                  <div className="glass-card flex-1 rounded-xl p-6 ring-1 ring-white/[0.06] transition-all group-hover:bg-white/[0.03]">
                    <h3 className="font-[family-name:var(--font-space-grotesk)] text-lg font-semibold text-white">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#94A3B8]">
                      {step.description}
                    </p>
                  </div>
                </m.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowIWork;
